import type { ReactNode } from 'react';
import NextLink from 'next/link';
import CloseIcon from '@mui/icons-material/Close';
import { Alert, Button, IconButton, Stack } from '@mui/material';

type DashboardActionAlertProps = Readonly<{
  actionHref: string;
  actionLabel: string;
  onClose: () => void;
  children: ReactNode;
}>;

/** A dismissible dashboard warning with a link to act on it. On phones the action row sits below the message, so the message keeps its width. */
export const DashboardActionAlert = ({ actionHref, actionLabel, onClose, children }: DashboardActionAlertProps) => (
  <Alert
    severity='warning'
    // A custom action replaces Alert's own close button, so the action slot carries both.
    action={
      <Stack direction='row' spacing={1} alignItems='center'>
        <Button component={NextLink} href={actionHref} color='warning' variant='outlined' size='small' sx={{ whiteSpace: 'nowrap' }}>
          {actionLabel}
        </Button>
        <IconButton aria-label='Close' color='inherit' size='small' onClick={onClose}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Stack>
    }
    sx={{
      mb: 3,
      borderRadius: 2,
      alignItems: 'center',
      flexWrap: { xs: 'wrap', sm: 'nowrap' },
      // The icon and its margin take 34px of the first line.
      '& .MuiAlert-message': { fontWeight: 500, flex: { xs: '1 1 calc(100% - 34px)', sm: 'initial' } },
      '& .MuiAlert-action': {
        width: { xs: '100%', sm: 'auto' },
        justifyContent: { xs: 'flex-end', sm: 'flex-start' },
        alignItems: { xs: 'center', sm: 'flex-start' },
        ml: { xs: 0, sm: 'auto' },
        pl: { xs: 0, sm: 2 },
      },
    }}
  >
    {children}
  </Alert>
);
